/**
 * Isomorphic redaction for structured logs (runs in Node and the browser).
 *
 * Two layers:
 *  - key-based: any object key matching SENSITIVE_KEYS / SENSITIVE_FRAGMENTS has
 *    its value replaced with [REDACTED]. This covers both fixed secret paths
 *    (authorization/cookie headers, API keys, DATABASE_URL) and PII fields
 *    (email_body, customer_first_name, thread_id, pii_findings[].quote, ...).
 *  - value-based: string leaves are scanned for emails, phone numbers and long
 *    digit runs (card / account numbers) and those substrings are masked, to
 *    catch free-text PII inside otherwise-innocent fields.
 *
 * The walk is depth-bounded, length-capped and cycle-guarded so it stays cheap
 * even on large, deeply-nested payloads (LangGraph state, a Fastify req, ...).
 *
 * NOTE: this scrubs structured FIELDS. Keep PII out of the free-text `message`
 * string itself — pino does not route the message through here.
 *
 * Redaction targets are driven by docs/gdpr-plan.md (names, emails, phones,
 * addresses, booking refs, payment-like strings, IDs) plus the secret names in
 * apps/aira-rag-backend/src/env.ts.
 */
const REDACTED = '[REDACTED]';
const DEFAULT_OPTIONS = {
    maxDepth: 6,
    maxStringLength: 8192,
    maxArrayLength: 100,
};
/** Exact key names (lower-cased) that always carry PII or secrets. */
const SENSITIVE_KEYS = new Set([
    'email',
    'email_body',
    'email_subject',
    'email_from',
    'email_to',
    'email_cc',
    'from_email',
    'to_email',
    'draft_body',
    'draft_text',
    'customer_first_name',
    'customer_last_name',
    'first_name',
    'last_name',
    'full_name',
    'phone',
    'phone_number',
    'address',
    'booking_ref',
    'booking_reference',
    'quote',
    'thread_id',
    'dob',
    'date_of_birth',
    'ssn',
    // Wrapper objects that carry post-redaction email snapshots — never log contents.
    'redacted',
]);
/** Substrings: any key containing one of these is treated as a secret. */
const SENSITIVE_FRAGMENTS = [
    'password',
    'passwd',
    'secret',
    'token',
    'api_key',
    'apikey',
    'authorization',
    'cookie',
    'jwt',
    'credential',
    'private_key',
    'access_key',
    'encryption_key',
    'client_secret',
    'database_url',
    'connection_string',
];
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const PHONE_RE = /\+\d[\d\s().-]{6,}\d/g;
const LONG_NUMBER_RE = /\b\d{13,19}\b/g;
/** Typed PII placeholders from pre-LLM redaction, e.g. [PERSON_NAME_1]. */
const PII_PLACEHOLDER_RE = /\[[A-Z][A-Z0-9_]*_\d+\]/g;
/**
 * Normalize a key to a canonical form so camelCase, snake_case, kebab-case and
 * PascalCase all compare equal: `emailBody`, `email_body` and `email-body` all
 * become `emailbody`. Without this, camelCase PII fields (threadId, emailBody,
 * customerFirstName) would slip past the snake_case deny-list.
 */
function normalizeKey(key) {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}
const SENSITIVE_KEYS_NORMALIZED = new Set([...SENSITIVE_KEYS].map(normalizeKey));
const SENSITIVE_FRAGMENTS_NORMALIZED = SENSITIVE_FRAGMENTS.map(normalizeKey);
/**
 * Token COUNTS (prompt_tokens, completion_tokens, token_count, max_output_tokens)
 * are telemetry, not secrets — so a `token` key is only sensitive when it's an
 * auth-token shape, not a count/cap/limit.
 */
function isBenignTokenKey(normalized) {
    return (normalized.includes('tokens') ||
        normalized.includes('tokencount') ||
        normalized.includes('tokencap') ||
        normalized.includes('tokenusage') ||
        normalized.includes('tokenlimit') ||
        normalized.includes('tokenbudget'));
}
/** True if a key name indicates a sensitive value (naming-style agnostic). */
export function isSensitiveKey(key) {
    const normalized = normalizeKey(key);
    if (SENSITIVE_KEYS_NORMALIZED.has(normalized)) {
        return true;
    }
    for (const fragment of SENSITIVE_FRAGMENTS_NORMALIZED) {
        if (!normalized.includes(fragment)) {
            continue;
        }
        // `token` over-matches token-count telemetry; keep those visible.
        if (fragment === 'token' && isBenignTokenKey(normalized)) {
            continue;
        }
        return true;
    }
    return false;
}
function maskString(value, maxLength) {
    const capped = value.length > maxLength ? `${value.slice(0, maxLength)}…[truncated]` : value;
    return capped
        .replace(EMAIL_RE, '[REDACTED_EMAIL]')
        .replace(PHONE_RE, '[REDACTED_PHONE]')
        .replace(LONG_NUMBER_RE, '[REDACTED_NUMBER]')
        .replace(PII_PLACEHOLDER_RE, REDACTED);
}
/**
 * Return a redacted deep copy of `value`. Primitives are returned as-is (strings
 * pattern-masked); objects/arrays are cloned with sensitive keys removed.
 */
export function scrub(value, options = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const seen = new WeakSet();
    function walk(input, depth) {
        if (input === null || input === undefined) {
            return input;
        }
        const type = typeof input;
        if (type === 'string') {
            return maskString(input, opts.maxStringLength);
        }
        if (type === 'number' || type === 'boolean' || type === 'bigint') {
            return input;
        }
        if (type === 'function' || type === 'symbol') {
            return undefined;
        }
        if (input instanceof Date) {
            return input;
        }
        if (input instanceof Error) {
            return {
                name: input.name,
                message: maskString(input.message, opts.maxStringLength),
                stack: input.stack ? maskString(input.stack, opts.maxStringLength) : undefined,
            };
        }
        if (depth >= opts.maxDepth) {
            return '[Truncated: max depth]';
        }
        if (typeof input === 'object') {
            if (seen.has(input)) {
                return '[Circular]';
            }
            seen.add(input);
            if (Array.isArray(input)) {
                const items = input.slice(0, opts.maxArrayLength).map((item) => walk(item, depth + 1));
                if (input.length > opts.maxArrayLength) {
                    items.push(`[Truncated: ${input.length - opts.maxArrayLength} more items]`);
                }
                return items;
            }
            const result = {};
            for (const [key, val] of Object.entries(input)) {
                result[key] = isSensitiveKey(key) ? REDACTED : walk(val, depth + 1);
            }
            return result;
        }
        return input;
    }
    return walk(value, 0);
}
//# sourceMappingURL=scrub.js.map